import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { firestoreService } from "@/services/firestoreService";
import { functionsService } from "@/services/functionsService";
import { TranscriptViewer } from "@/components/analysis/TranscriptViewer";
import { FilingViewer } from "@/components/analysis/FilingViewer";
import { CompetitiveRadarChart } from "@/components/CompetitiveRadarChart";
import { StockQuote } from "@/components/StockQuote";
import { PriceChart } from "@/components/PriceChart";
import { FinancialStatements } from "@/components/FinancialStatements";
import { KeyMetrics } from "@/components/KeyMetrics";

interface Company {
  id: string;
  name: string;
  ticker: string;
  sector?: string;
  industry?: string;
  logo?: string;
  description?: string;
}

interface Transcript {
  id: string;
  quarter: number;
  year: number;
  date: any;
}

interface Filing {
  id: string;
  filingType: string;
  date: any;
  sourceUrl?: string;
}

interface NewsArticle {
  id: string;
  title: string;
  summary?: string;
  source: string;
  url?: string;
  publishedAt: any;
  ticker?: string;
}

interface PressRelease {
  id: string;
  title: string;
  content?: string;
  text?: string;
  date: any;
  source?: string;
  url?: string;
}

/** Latest Firestore row for company-scoped rankings (influential = CEO, polarizing). */
interface CompanyRankingSnapshot {
  id: string;
  rank?: number;
  score: number;
  name?: string;
  explanation?: string;
  ticker?: string;
}

export const CompanyDetail: React.FC = () => {
  const { ticker } = useParams<{ ticker: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState<Company | null>(null);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [news, setNews] = useState<NewsArticle[]>([]);
  const [pressReleases, setPressReleases] = useState<PressRelease[]>([]);
  const [radarChartData, setRadarChartData] = useState<Array<{ dimension: string; score: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<
    | "overview"
    | "transcripts"
    | "filings"
    | "news"
    | "releases"
    | "ceo"
    | "polarization"
  >("overview");
  const [ceoRanking, setCeoRanking] = useState<CompanyRankingSnapshot | null>(null);
  const [polarizationRanking, setPolarizationRanking] =
    useState<CompanyRankingSnapshot | null>(null);
  const [selectedTranscript, setSelectedTranscript] = useState<string | null>(
    null
  );
  const [selectedFiling, setSelectedFiling] = useState<string | null>(null);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);

  useEffect(() => {
    if (ticker) {
      loadCompanyData();
    }
  }, [ticker]);

  /** Reload transcripts for current company (used after on-demand fetch). */
  const loadTranscriptsForCompany = async (companyId: string) => {
    const data = await firestoreService.getDocuments<Transcript>("transcripts", [
      firestoreService.where("companyId", "==", companyId),
      firestoreService.orderBy("date", "desc"),
      firestoreService.limit(100),
    ]).catch(() => []);
    setTranscripts(data);
  };

  /** When user opens the transcripts tab: fetch transcripts for this company (on-demand), then refresh list. */
  useEffect(() => {
    if (activeView !== "transcripts" || !ticker) return;
    const companyId = ticker.toUpperCase();
    let cancelled = false;
    (async () => {
      setTranscriptsLoading(true);
      try {
        await functionsService.fetchCompanyTranscripts(companyId);
        if (!cancelled) {
          await loadTranscriptsForCompany(companyId);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("On-demand transcript fetch failed:", err);
        }
      } finally {
        if (!cancelled) setTranscriptsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeView, ticker]);

  const loadCompanyData = async () => {
    try {
      setLoading(true);

      // Try to get company by document ID first (ticker is the document ID)
      let companyData: Company | null = null;

      if (ticker) {
        try {
          const companyDoc = await firestoreService.getDocument<Company>(
            "companies",
            ticker.toUpperCase()
          );
          if (companyDoc) {
            companyData = companyDoc;
          }
        } catch (error) {
          console.error("Failed to get company by ID:", error);
        }
      }

      // If not found by ID, try querying by ticker field
      if (!companyData && ticker) {
        const companies = await firestoreService.getDocuments<Company>(
          "companies",
          [
            firestoreService.where("ticker", "==", ticker.toUpperCase()),
            firestoreService.limit(1),
          ]
        );
        if (companies.length > 0) {
          companyData = companies[0];
        }
      }

      if (!companyData) {
        throw new Error("Company not found");
      }

      setCompany(companyData);

      // Fetch and update company profile from FMP API
      try {
        const profileResult = await functionsService.getCompanyProfile(
          companyData.ticker
        );
        if (profileResult.success && profileResult.profile) {
          // Update local company state with fresh data
          setCompany({
            ...companyData,
            name: profileResult.profile.companyName || companyData.name,
            sector: profileResult.profile.sector || companyData.sector,
            industry: profileResult.profile.industry || companyData.industry,
            description:
              profileResult.profile.description || companyData.description,
            logo: profileResult.profile.image || companyData.logo,
          });
        }
      } catch (error) {
        console.error("Failed to fetch company profile from FMP:", error);
        // Continue with Firestore data if FMP fails
      }

      // Use ticker for queries (backend stores companyId = ticker)
      const companyId = (companyData.ticker || companyData.id || "").toString().toUpperCase();

      const [transcriptsData, filingsData, newsData, releasesByCompanyId, releasesByTicker, analysesData] =
        await Promise.all([
          firestoreService
            .getDocuments<Transcript>("transcripts", [
              firestoreService.where("companyId", "==", companyId),
              firestoreService.orderBy("date", "desc"),
              firestoreService.limit(100),
            ])
            .catch(() => []),
          firestoreService
            .getDocuments<Filing>("filings", [
              firestoreService.where("companyId", "==", companyId),
              firestoreService.orderBy("date", "desc"),
              firestoreService.limit(20),
            ])
            .catch(() => []),
          firestoreService
            .getDocuments<NewsArticle>("news", [
              firestoreService.where(
                "ticker",
                "==",
                companyData.ticker.toUpperCase()
              ),
              firestoreService.orderBy("publishedAt", "desc"),
              firestoreService.limit(20),
            ])
            .catch(() => []),
          firestoreService
            .getDocuments<PressRelease>("pressReleases", [
              firestoreService.where("companyId", "==", companyId),
              firestoreService.orderBy("date", "desc"),
              firestoreService.limit(20),
            ])
            .catch(() => []),
          firestoreService
            .getDocuments<PressRelease>("pressReleases", [
              firestoreService.where("ticker", "==", companyId),
              firestoreService.limit(20),
            ])
            .catch(() => []),
          firestoreService
            .getDocuments<{ analysis?: { radarChart?: any } }>("transcriptAnalyses", [
              firestoreService.where("companyId", "==", companyId),
              firestoreService.orderBy("date", "desc"),
              firestoreService.limit(10),
            ])
            .catch(() => []),
        ]);

      setTranscripts(transcriptsData);
      setFilings(filingsData);
      setNews(newsData);
      const mergedReleases = (releasesByCompanyId.length > 0 ? releasesByCompanyId : releasesByTicker)
        .map((r) => ({
          ...r,
          content: r.content || r.text || "",
        }))
        .sort((a, b) => {
          const ad = new Date(a.date?.toDate?.() || a.date).getTime();
          const bd = new Date(b.date?.toDate?.() || b.date).getTime();
          return bd - ad;
        })
        .slice(0, 20);
      setPressReleases(mergedReleases);

      const [ceoRankDocs, polarRankDocs] = await Promise.all([
        firestoreService
          .getDocuments<CompanyRankingSnapshot>("rankings", [
            firestoreService.where("companyId", "==", companyId),
            firestoreService.where("type", "==", "influential"),
            firestoreService.orderBy("date", "desc"),
            firestoreService.limit(1),
          ])
          .catch(() => []),
        firestoreService
          .getDocuments<CompanyRankingSnapshot>("rankings", [
            firestoreService.where("companyId", "==", companyId),
            firestoreService.where("type", "==", "polarizing"),
            firestoreService.orderBy("date", "desc"),
            firestoreService.limit(1),
          ])
          .catch(() => []),
      ]);
      setCeoRanking(ceoRankDocs[0] ?? null);
      setPolarizationRanking(polarRankDocs[0] ?? null);

      const transcriptIdFromUrl = searchParams.get("transcript");
      if (transcriptIdFromUrl && transcriptsData.some((t) => t.id === transcriptIdFromUrl)) {
        setSelectedTranscript(transcriptIdFromUrl);
      }
      const filingIdFromUrl = searchParams.get("filing");
      if (filingIdFromUrl && filingsData.some((f) => f.id === filingIdFromUrl)) {
        setSelectedFiling(filingIdFromUrl);
        setActiveView("filings");
      }
      
      // Calculate average radar chart scores from transcript analyses
      if (analysesData.length > 0) {
        const radarScores = {
          strategicPlanning: [] as number[],
          organizationalLeadership: [] as number[],
          communicationSkills: [] as number[],
          externalAdaptability: [] as number[],
          innovationCapability: [] as number[],
        };

        analysesData.forEach((analysis) => {
          const radarChart = analysis.analysis?.radarChart;
          if (radarChart) {
            if (radarChart.strategicPlanning !== undefined) {
              radarScores.strategicPlanning.push(radarChart.strategicPlanning);
            }
            if (radarChart.organizationalLeadership !== undefined) {
              radarScores.organizationalLeadership.push(radarChart.organizationalLeadership);
            }
            if (radarChart.communicationSkills !== undefined) {
              radarScores.communicationSkills.push(radarChart.communicationSkills);
            }
            if (radarChart.externalAdaptability !== undefined) {
              radarScores.externalAdaptability.push(radarChart.externalAdaptability);
            }
            if (radarChart.innovationCapability !== undefined) {
              radarScores.innovationCapability.push(radarChart.innovationCapability);
            }
          }
        });

        const calculateAverage = (scores: number[]) => {
          if (scores.length === 0) return 0;
          return scores.reduce((a, b) => a + b, 0) / scores.length;
        };

        setRadarChartData([
          { dimension: "Strategic Planning", score: Math.round(calculateAverage(radarScores.strategicPlanning)) },
          { dimension: "Organizational Leadership", score: Math.round(calculateAverage(radarScores.organizationalLeadership)) },
          { dimension: "Communication Skills", score: Math.round(calculateAverage(radarScores.communicationSkills)) },
          { dimension: "External Adaptability", score: Math.round(calculateAverage(radarScores.externalAdaptability)) },
          { dimension: "Innovation Capability", score: Math.round(calculateAverage(radarScores.innovationCapability)) },
        ]);
      }

    } catch (error) {
      console.error("Failed to load company data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blazerex-green"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <Card>
            <div className="text-center py-12">
              <p className="body-text text-gray-600 mb-4">Company not found</p>
              <Button variant="primary" onClick={() => navigate("/companies")}>
                Back to Companies
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (selectedTranscript) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <Button
            variant="outline"
            onClick={() => setSelectedTranscript(null)}
            className="mb-4"
          >
            ← Back to Company
          </Button>
          <TranscriptViewer transcriptId={selectedTranscript} />
        </div>
      </div>
    );
  }

  if (selectedFiling) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <Button
            variant="outline"
            onClick={() => setSelectedFiling(null)}
            className="mb-4"
          >
            ← Back to Company
          </Button>
          <FilingViewer filingId={selectedFiling} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <Button
            variant="outline"
            onClick={() => navigate("/companies")}
            className="mb-4"
          >
            ← Back to Companies
          </Button>
          <div className="flex items-start gap-6">
            {company.logo && (
              <img
                src={company.logo}
                alt={company.name}
                className="w-24 h-24 rounded"
              />
            )}
            <div>
              <h1 className="h1 text-4xl mb-2">{company.name}</h1>
              <p className="body-text text-gray-600 font-mono text-lg mb-2">
                {company.ticker}
              </p>
              {company.sector && (
                <p className="body-text text-sm text-gray-500">
                  {company.sector} {company.industry && `• ${company.industry}`}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200 overflow-x-auto">
          {[
            { key: "overview", label: "Overview" },
            {
              key: "transcripts",
              label:
                transcripts.length > 0
                  ? `Earnings (${transcripts.length})`
                  : transcriptsLoading
                    ? "Earnings (…)"
                    : "Earnings",
            },
            { key: "filings", label: `Filings (${filings.length})` },
            { key: "news", label: `News (${news.length})` },
            { key: "releases", label: `Releases (${pressReleases.length})` },
            { key: "ceo", label: "CEO" },
            { key: "polarization", label: "Polarization" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveView(tab.key as any)}
              className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap ${
                activeView === tab.key
                  ? "border-blazerex-green text-blazerex-green font-header-bold"
                  : "border-transparent text-gray-600 hover:text-blazerex-green"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeView === "overview" && (
          <div className="space-y-6">
            <StockQuote ticker={company.ticker} />
            <PriceChart ticker={company.ticker} />
            <KeyMetrics ticker={company.ticker} />
            <FinancialStatements ticker={company.ticker} />
            <Card>
              {company.description ? (
                <div>
                  <h3 className="sub-header text-xl mb-4">About</h3>
                  <p className="body-text text-gray-700">
                    {company.description}
                  </p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">
                    No company description available
                  </p>
                </div>
              )}
            </Card>
            {radarChartData.length > 0 ? (
              <Card>
                <CompetitiveRadarChart
                  companyName={company.name}
                  data={radarChartData}
                />
              </Card>
            ) : (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">
                    Radar chart data will appear here once earnings call transcripts are analyzed.
                  </p>
                </div>
              </Card>
            )}
          </div>
        )}

        {activeView === "transcripts" && (
          <div className="space-y-4">
            {transcriptsLoading && (
              <p className="body-text text-sm text-gray-500">Checking for latest transcripts…</p>
            )}
            {transcripts.length === 0 && !transcriptsLoading ? (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600 mb-2">
                    No earnings call transcripts available for this company.
                  </p>
                  <p className="body-text text-sm text-gray-500">
                    Transcripts are added when we process SEC filings and earnings data. Check back later or try another company.
                  </p>
                </div>
              </Card>
            ) : transcripts.length === 0 ? null : (
              transcripts.map((transcript) => (
                <Card key={transcript.id} hover>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="sub-header text-lg mb-1">
                        Q{transcript.quarter} {transcript.year}
                      </h3>
                      <p className="body-text text-sm text-gray-600">
                        {new Date(
                          transcript.date?.toDate?.() || transcript.date
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => setSelectedTranscript(transcript.id)}
                    >
                      View & Analyze
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeView === "filings" && (
          <div className="space-y-4">
            {filings.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">
                    No filings available
                  </p>
                </div>
              </Card>
            ) : (
              filings.map((filing) => (
                <Card key={filing.id} hover>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h3 className="sub-header text-lg mb-1">
                        {filing.filingType}
                      </h3>
                      <p className="body-text text-sm text-gray-600">
                        {new Date(
                          filing.date?.toDate?.() || filing.date
                        ).toLocaleDateString()}
                      </p>
                      {filing.sourceUrl && (
                        <a
                          href={filing.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blazerex-green hover:underline mt-1 inline-block"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View on SEC.gov →
                        </a>
                      )}
                    </div>
                    <Button
                      variant="primary"
                      onClick={() => setSelectedFiling(filing.id)}
                    >
                      View & Summarise
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeView === "news" && (
          <div className="space-y-4">
            {news.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">
                    No news available for this company
                  </p>
                </div>
              </Card>
            ) : (
              news.map((article) => (
                <Card key={article.id} hover>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="sub-header text-lg mb-2">
                        {article.title}
                      </h3>
                      {article.summary && (
                        <p className="body-text text-gray-600 mb-3">
                          {article.summary}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{article.source}</span>
                        {article.publishedAt && (
                          <span>
                            {new Date(
                              article.publishedAt?.toDate?.() ||
                                article.publishedAt
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {article.url && (
                      <a
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blazerex-green hover:underline"
                      >
                        Read More →
                      </a>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeView === "releases" && (
          <div className="space-y-4">
            {pressReleases.length === 0 ? (
              <Card>
                <div className="text-center py-12">
                  <p className="body-text text-gray-600">
                    No press releases available for this company
                  </p>
                </div>
              </Card>
            ) : (
              pressReleases.map((release) => (
                <Card key={release.id} hover>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="sub-header text-lg mb-2">
                        {release.title}
                      </h3>
                      <p className="body-text text-gray-700 mb-3 line-clamp-3">
                        {release.content}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        {release.source && <span>{release.source}</span>}
                        {release.date && (
                          <span>
                            {new Date(
                              release.date?.toDate?.() || release.date
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {release.url && (
                      <a
                        href={release.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blazerex-green hover:underline"
                      >
                        Read More →
                      </a>
                    )}
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {activeView === "ceo" && (
          <Card>
            <h3 className="sub-header text-xl mb-6">CEO Ranking</h3>
            {ceoRanking ? (
              <div className="flex flex-wrap gap-8 max-w-lg">
                <div>
                  <p className="body-text text-sm text-gray-600 mb-1">Rank</p>
                  <p className="h2 text-3xl text-blazerex-green font-header-bold">
                    #{ceoRanking.rank ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="body-text text-sm text-gray-600 mb-1">Score</p>
                  <p className="h2 text-3xl text-blazerex-green font-header-bold">
                    {typeof ceoRanking.score === "number"
                      ? ceoRanking.score.toFixed(1)
                      : "—"}
                  </p>
                </div>
                {ceoRanking.explanation && (
                  <p className="body-text text-gray-700 w-full basis-full">
                    {ceoRanking.explanation}
                  </p>
                )}
              </div>
            ) : (
              <p className="body-text text-gray-600">
                No CEO ranking is available for this company yet. Rankings are updated when earnings
                analyses include leadership radar scores.
              </p>
            )}
          </Card>
        )}

        {activeView === "polarization" && (
          <Card>
            <h3 className="sub-header text-xl mb-6">Polarization Ranking</h3>
            {polarizationRanking ? (
              <div className="flex flex-wrap gap-8 max-w-lg">
                <div>
                  <p className="body-text text-sm text-gray-600 mb-1">Rank</p>
                  <p className="h2 text-3xl text-blazerex-green font-header-bold">
                    #{polarizationRanking.rank ?? "—"}
                  </p>
                </div>
                <div>
                  <p className="body-text text-sm text-gray-600 mb-1">Score</p>
                  <p className="h2 text-3xl text-blazerex-green font-header-bold">
                    {typeof polarizationRanking.score === "number"
                      ? polarizationRanking.score.toFixed(1)
                      : "—"}
                  </p>
                </div>
                {polarizationRanking.explanation && (
                  <p className="body-text text-gray-700 w-full basis-full">
                    {polarizationRanking.explanation}
                  </p>
                )}
              </div>
            ) : (
              <p className="body-text text-gray-600">
                No polarization ranking is available for this company yet.
              </p>
            )}
          </Card>
        )}

      </div>
    </div>
  );
};
